import { useState, useRef, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { toast } from '../components/ui/sonner';

/**
 * Unified Camera hook that works on both native (Capacitor) and web platforms.
 *
 * On web: Uses navigator.mediaDevices.getUserMedia (existing behavior).
 * On native: Also uses getUserMedia for live video stream (needed for face
 * verification and live preview), but CAN use @capacitor/camera for
 * quick photo capture if needed in the future.
 *
 * The live video stream approach is preserved on native because AbsensiPage
 * requires real-time video preview with watermark overlay and face verification.
 */
export const useCamera = () => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [facingMode, setFacingMode] = useState('user');

  const isNative = Capacitor.isNativePlatform();

  /**
   * Initialize camera stream. On native, Capacitor's webview provides access
   * to getUserMedia as well, so we use the same API across platforms.
   * If native permissions are needed, we request them through the Camera plugin.
   */
  const initCamera = useCallback(async () => {
    try {
      // On native, ensure camera permission is granted via Capacitor
      if (isNative) {
        try {
          const { Camera } = await import('@capacitor/camera');
          const permResult = await Camera.checkPermissions();
          if (permResult.camera !== 'granted') {
            const reqResult = await Camera.requestPermissions({ permissions: ['camera'] });
            if (reqResult.camera !== 'granted') {
              toast.error('Izin kamera ditolak. Aktifkan kamera di pengaturan.');
              return;
            }
          }
        } catch (permErr) {
          console.warn('[Camera] Native permission check failed, trying getUserMedia directly:', permErr);
        }
      }

      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode, width: { ideal: 640 }, height: { ideal: 480 } },
        audio: false
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (error) {
      console.error('Camera error:', error);
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        toast.error('Izin kamera ditolak. Aktifkan kamera di pengaturan perangkat.');
      } else if (error.name === 'NotFoundError') {
        toast.error('Kamera tidak ditemukan. Pastikan perangkat memiliki kamera.');
      } else if (error.name === 'NotReadableError') {
        toast.error('Kamera sedang digunakan aplikasi lain.');
      } else {
        toast.error('Gagal mengakses kamera. Pastikan izin kamera diberikan.');
      }
    }
  }, [facingMode, isNative]);

  const takePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    if (facingMode === 'user') {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
    setPhoto(dataUrl);
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
  }, [facingMode, stream]);

  const retakePhoto = useCallback(() => {
    setPhoto(null);
    initCamera();
  }, [initCamera]);

  const toggleCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
    setFacingMode(prev => prev === 'user' ? 'environment' : 'user');
  }, [stream]);

  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
  }, [stream]);

  return {
    videoRef, canvasRef, stream, photo, facingMode,
    initCamera, takePhoto, retakePhoto, toggleCamera, stopCamera,
  };
};
